import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Popover,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import config from "../../../config";
import {
  PTButton,
  PTIcon,
  PTTranslate,
  PTTypography,
} from "../../components/ui";
import { useLanguage } from "../../hooks";
import {
  useLoadNotification,
  useReadAllNotification,
  useReadNotification,
} from "../../hooks/useNotification";

export const PTNotificationIcon = () => {
  const [notificationCount, setNotificationCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [page, setPage] = useState(1);
  const open = Boolean(anchorEl);
  const { currentLang } = useLanguage();
  const {
    data: data,
    isLoading: loading,
    refetch,
  } = useLoadNotification(open, page - 1, 5); // Adjust for 0-based indexing
  const { mutate: readNotification, isFetching } = useReadNotification();
  const { mutate: readAll, isFetching: isFetchingAll } =
    useReadAllNotification();
  const notifications = data?.content || [];
  const totalPages = data?.totalPages || 1;
  const isRTL = currentLang === "ar";
  const navigate = useNavigate();

  // Mark notification as read
  const markAsRead = async (id) => {
    try {
      readNotification(id);
      handleActionMenuClose();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      readAll();
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  // Handle pagination change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  useEffect(() => {
    const authToken = localStorage.getItem("authToken");

    // WebSocket connection
    const ws = new WebSocket(
      `${config.wsUrl}/ws-notifications?token=${authToken}`
    );

    ws.onopen = () => {
      console.log("Connected to notification service");
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.count !== undefined) {
        setNotificationCount(data.count);
      }
    };

    ws.onclose = () => {
      console.log("Disconnected from notification service");
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    // Clean up the WebSocket connection when component unmounts
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, []);

  // Refetch data when page changes
  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [page, open, refetch]);

  // Handle click on notification icon
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setPage(1); // Reset to first page when opening
  };

  // Handle close
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Open action menu for a notification
  const handleActionMenuOpen = (event, notification) => {
    event.stopPropagation();
    setSelectedNotification(notification);
    setActionMenuAnchorEl(event.currentTarget);
  };

  // Close action menu
  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
    setSelectedNotification(null);
  };

  // Handle notification click
  const handleNotificationClick = (item) => {
    const { id, link } = item;
    if (link) {
      navigate(link);
    }
    if (!!id) markAsRead(id);
  };

  // Handle direct navigation click
  const handleNavigateClick = (event, notification) => {
    event.stopPropagation();
    const { id, link } = notification;
    if (link) {
      navigate(link);
      if (!!id) markAsRead(id);
    }
  };

  // Handle direct mark as read
  const handleMarkAsReadClick = (event, notification) => {
    event.stopPropagation();
    if (notification?.id) {
      markAsRead(notification.id);
    }
  };

  // Get icon and color based on notification type
  const getNotificationStyle = (typeCode) => {
    switch (typeCode?.toLowerCase()) {
      case "alert":
        return { icon: "warning", color: "#FF9800" };
      case "error":
        return { icon: "error", color: "#F44336" };
      case "action":
        return { icon: "assignment", color: "#3F51B5" };
      case "success":
        return { icon: "check_circle", color: "#4CAF50" };
      case "info":
      default:
        return { icon: "info", color: "#2196F3" };
    }
  };
  const actionMenuOpen = Boolean(actionMenuAnchorEl);
  const id = open ? "notifications-popover" : undefined;

  // Count unread notifications
  const unreadCount =
    notifications?.filter((notification) => !notification.read).length || 0;

  return (
    <Box sx={{ position: "relative" }}>
      <PTIcon
        icon="notification"
        tooltip="notification.name"
        color={"#FFFFFF"}
        onClick={handleClick}
        sx={{
          cursor: "pointer",
          "&:hover": {
            color: "#25384a",
          },
          mb: "-3px",
        }}
      />
      {notificationCount > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: "-12px",
            right: "-8px",
            backgroundColor: "#FF725E",
            color: "#FFFFFF",
            borderRadius: "50%",
            width: "16px",
            height: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "10px",
            fontWeight: "bold",
          }}
        >
          {notificationCount > 99 ? "99+" : notificationCount}
        </Box>
      )}

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            width: { xs: "80vw", sm: 420 },
            maxHeight: 500,
            overflowY: "auto",
            [isRTL ? "mr" : "ml"]: "12vw",
            mt: 1.5,
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
            borderRadius: "8px",
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#f5f5f5",
          }}
        >
          <PTTypography variant="h6" sx={{ fontWeight: "bold" }}>
            notification.list
          </PTTypography>
          <Box>
            <IconButton
              size="small"
              onClick={() => refetch()}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={20} />
              ) : (
                <PTIcon icon="refresh" color="#333" />
              )}
            </IconButton>
          </Box>
        </Box>

        {/* Mark as Read button at the top */}
        {unreadCount > 0 && (
          <Box
            sx={{
              px: 2,
              py: 1,
              bgcolor: "#f0f7ff",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <PTTypography variant="body2">
              {unreadCount} <PTTranslate tk={"notification.unread"} />{" "}
              <PTTranslate
                tk={
                  unreadCount === 1 ? "notification.name" : "notification.list"
                }
              />
            </PTTypography>
            <PTButton
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<PTIcon icon="done_all" />}
              onClick={markAllAsRead}
              title="notification.markAllAsRead"
              sx={{ height: 28 }}
            />
          </Box>
        )}

        <Divider />

        {loading ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <CircularProgress size={30} />
          </Box>
        ) : notifications?.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <PTTypography color="textSecondary">
              notification.noNotificationsFound
            </PTTypography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications?.map((notification, index) => {
              const style = getNotificationStyle(notification.typeCode);
              const hasLink = !!notification.link;
              const isUnread = !notification.read;

              // Determine what actions are available
              const canMarkAsRead = isUnread;
              const canNavigate = hasLink;
              const actionCount =
                (canMarkAsRead ? 1 : 0) + (canNavigate ? 1 : 0);

              // Determine which icon to show
              let actionIcon = null;

              if (actionCount > 1) {
                // Multiple actions available - show more_vert menu
                actionIcon = (
                  <IconButton
                    size="small"
                    onClick={(e) => handleActionMenuOpen(e, notification)}
                    sx={{
                      mt: 0.5,
                      color: "#666",
                      "&:hover": {
                        color: "#333",
                        bgcolor: "rgba(0, 0, 0, 0.08)",
                      },
                    }}
                  >
                    <PTIcon
                      icon="more_vert"
                      tooltip="notification.more_actions"
                    />
                  </IconButton>
                );
              } else if (canNavigate) {
                // Only navigation action available
                actionIcon = (
                  <IconButton
                    size="small"
                    onClick={(e) => handleNavigateClick(e, notification)}
                    sx={{
                      mt: 0.5,
                      color: "#2196F3",
                      "&:hover": {
                        color: "#1976D2",
                        bgcolor: "rgba(33, 150, 243, 0.08)",
                      },
                    }}
                  >
                    <PTIcon icon="open_in_new" tooltip="notification.open" />
                  </IconButton>
                );
              } else if (canMarkAsRead) {
                // Only mark as read action available
                actionIcon = (
                  <IconButton
                    size="small"
                    onClick={(e) => handleMarkAsReadClick(e, notification)}
                    sx={{
                      mt: 0.5,
                      color: "#4CAF50",
                      "&:hover": {
                        color: "#388E3C",
                        bgcolor: "rgba(76, 175, 80, 0.08)",
                      },
                    }}
                  >
                    <PTIcon icon="done" tooltip="notification.markAsRead" />
                  </IconButton>
                );
              }

              return (
                <div key={notification.id || index}>
                  <ListItem
                    sx={{
                      py: 1.5,
                      px: 2,
                      "&:hover": {
                        bgcolor: "rgba(0, 0, 0, 0.04)",
                      },
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "flex-start",
                      bgcolor: isUnread
                        ? "rgba(0, 120, 255, 0.04)"
                        : "transparent",
                      borderLeft:
                        isUnread && !isRTL ? "3px solid #2196F3" : "none",
                      borderRight:
                        isUnread && isRTL ? "3px solid #2196F3" : "none",
                    }}
                  >
                    <Box
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        display: "flex",
                        flex: 1,
                        flexDirection: isRTL ? "row-reverse" : "row",
                        "&:hover": {
                          textDecoration: "none",
                        },
                      }}
                    >
                      {!isRTL && (
                        <Box
                          sx={{
                            mr: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: 40,
                            width: 40,
                            borderRadius: "50%",
                            bgcolor: `${style.color}20`,
                            color: style.color,
                            flexShrink: 0,
                          }}
                        >
                          <PTIcon icon={style.icon} />
                        </Box>
                      )}
                      <ListItemText
                        primary={
                          <PTTypography
                            variant="subtitle2"
                            sx={{
                              fontWeight: isUnread ? "bold" : "normal",
                              pr: 2,
                              color: isUnread ? "#000" : "#555",
                              textAlign: isRTL ? "right" : "left",
                            }}
                          >
                            {notification.title}
                          </PTTypography>
                        }
                        secondary={
                          <PTTypography
                            variant="body2"
                            color="textSecondary"
                            sx={{ textAlign: isRTL ? "right" : "left" }}
                          >
                            {notification.message}
                          </PTTypography>
                        }
                      />
                      {isRTL && (
                        <Box
                          sx={{
                            ml: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: 40,
                            width: 40,
                            borderRadius: "50%",
                            bgcolor: `${style.color}20`,
                            color: style.color,
                            flexShrink: 0,
                          }}
                        >
                          <PTIcon icon={style.icon} />
                        </Box>
                      )}
                    </Box>

                    {/* Display the appropriate action icon or menu */}
                    {actionIcon}
                  </ListItem>
                  {index < notifications?.length - 1 && <Divider />}
                </div>
              );
            })}
          </List>
        )}

        {/* Pagination component at the bottom */}
        {totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: 1.5,
              bgcolor: "#f5f5f5",
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              size="small"
              color="primary"
              siblingCount={1}
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Popover>

      {/* Action Menu for individual notifications */}
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={actionMenuOpen}
        onClose={handleActionMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {selectedNotification && !selectedNotification.read && (
          <MenuItem
            onClick={() =>
              selectedNotification && markAsRead(selectedNotification.id)
            }
            dense
          >
            <PTIcon
              icon="done"
              tooltip={"notification.markAsRead"}
              sx={{ mr: 1, fontSize: 18 }}
            />
            <PTTranslate tk="notification.markAsRead" />
          </MenuItem>
        )}
        {selectedNotification?.link && (
          <MenuItem
            onClick={() => {
              if (selectedNotification.link) {
                navigate(selectedNotification.link);
                if (selectedNotification.id)
                  markAsRead(selectedNotification.id);
              }
              handleActionMenuClose();
            }}
            dense
          >
            <PTIcon
              icon="open_in_new"
              tooltip={"notification.open"}
              sx={{ mr: 1, fontSize: 18 }}
            />
            <PTTranslate tk="notification.open" />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default PTNotificationIcon;
